import type { ReactNode } from 'react';

interface PlanStep {
  order: number;
  title: string;
  description: string;
  files_to_touch: string[];
  operation: string;
}

interface ExecutionPlan {
  plan: {
    summary: string;
    estimated_complexity: string;
    risks: string[];
    steps: PlanStep[];
  };
}

const operationColor: Record<string, string> = {
  CREATE: 'var(--mds_brand_appearance_positive_default_text-color, #16a34a)',
  MODIFY: 'var(--mds_brand_appearance_caution_default_text-color, #b45309)',
  DELETE: 'var(--mds_brand_appearance_error_default_text-color, #dc2626)',
  RENAME: 'var(--mds_brand_appearance_info_default_text-color, #6d28d9)',
};

function parseExecutionPlan(value: string): ExecutionPlan | null {
  try {
    const parsed = JSON.parse(value);
    if (parsed?.plan?.steps) return parsed as ExecutionPlan;
    return null;
  } catch {
    return null;
  }
}

export function ExecutionPlanView(value: string): ReactNode {
  const data = parseExecutionPlan(value);

  if (!data) {
    return <p style={{ margin: 0, lineHeight: 1.6 }}>{value}</p>;
  }

  const { plan } = data;
  const complexity = plan.estimated_complexity?.toUpperCase() ?? '—';
  const complexityColor =
    complexity === 'LOW'
      ? 'var(--mds_brand_appearance_positive_default_text-color, #16a34a)'
      : complexity === 'MEDIUM'
        ? 'var(--mds_brand_appearance_caution_default_text-color, #b45309)'
        : 'var(--mds_brand_appearance_error_default_text-color, #dc2626)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Summary + complexity */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
        <p style={{ margin: 0, lineHeight: 1.6, fontStyle: 'italic', color: 'var(--mds_brand_appearance_neutral_weak_text-color)', flex: 1 }}>
          {plan.summary}
        </p>
        <span
          style={{
            flexShrink: 0,
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.05em',
            padding: '2px 8px',
            borderRadius: '4px',
            border: `1px solid ${complexityColor}`,
            color: complexityColor,
            whiteSpace: 'nowrap',
          }}
        >
          {complexity}
        </span>
      </div>

      {/* Risks */}
      {plan.risks?.length > 0 && (
        <div>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--mds_brand_appearance_neutral_weak_text-color)', marginBottom: '0.35rem' }}>
            Risks
          </div>
          <ul style={{ margin: 0, paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            {plan.risks.map((risk, i) => (
              <li key={i} style={{ fontSize: '0.8rem', lineHeight: 1.5, color: 'var(--mds_brand_appearance_caution_default_text-color, #b45309)' }}>
                {risk}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Steps */}
      {plan.steps?.length > 0 && (
        <div>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--mds_brand_appearance_neutral_weak_text-color)', marginBottom: '0.5rem' }}>
            Steps
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {plan.steps.map((step) => {
              const opColor = operationColor[step.operation] ?? 'var(--mds_brand_appearance_neutral_default_text-color)';
              return (
                <div
                  key={step.order}
                  style={{
                    display: 'flex',
                    gap: '0.75rem',
                    padding: '0.6rem 0.75rem',
                    borderRadius: '6px',
                    backgroundColor: 'var(--mds_brand_appearance_neutral_weak_background-color, #f0f2f5)',
                    border: '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #e0e0e0)',
                  }}
                >
                  {/* Order badge */}
                  <div
                    style={{
                      flexShrink: 0,
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--mds_brand_appearance_primary_default_background-color, #0073ab)',
                      color: '#fff',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: '1px',
                    }}
                  >
                    {step.order}
                  </div>

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    {/* Title + operation */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{step.title}</span>
                      <span
                        style={{
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          letterSpacing: '0.05em',
                          padding: '1px 6px',
                          borderRadius: '3px',
                          border: `1px solid ${opColor}`,
                          color: opColor,
                        }}
                      >
                        {step.operation}
                      </span>
                    </div>

                    {/* Description */}
                    <p style={{ margin: 0, fontSize: '0.8rem', lineHeight: 1.5, color: 'var(--mds_brand_appearance_neutral_weak_text-color)' }}>
                      {step.description}
                    </p>

                    {/* Files */}
                    {step.files_to_touch?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.1rem' }}>
                        {step.files_to_touch.map((file) => (
                          <span
                            key={file}
                            style={{
                              fontFamily: 'monospace',
                              fontSize: '0.7rem',
                              padding: '1px 6px',
                              borderRadius: '3px',
                              backgroundColor: 'var(--mds_brand_appearance_neutral_weak_background-color, #f0f2f5)',
                              color: 'var(--mds_brand_appearance_neutral_default_text-color)',
                              border: '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #e0e0e0)',
                            }}
                          >
                            {file}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
